import { useEffect, useRef } from "react";
import { CommonPage, Content, LoadableContent, PageTitle } from "common/Grid";
import { toast } from "react-toastify";
import { SalesLeadForm } from "../Common/MainForm";
import { useUpdateSalesLeadMutation, useGetSalesLeadsListQuery } from "store/action/salesLeadsApi";
import { useNavigate, useParams } from "react-router-dom";
import { captureComponentError } from "utils/sentry";

const cleanText = (value) => String(value ?? "").trim();

const EditSalesLead = () => {
  const { id } = useParams();
  const [updateSalesLead, { isLoading: isUpdating }] = useUpdateSalesLeadMutation();
  const navigate = useNavigate();
  const successRef = useRef(false);

  useEffect(() => {
    return () => {
      if (!successRef.current) {
        toast.dismiss();
      }
    };
  }, []);

  const { data: apiResponse, isLoading: isFetching, isError } = useGetSalesLeadsListQuery({ _id: id });
  const lead = apiResponse?.data?.find((item) => item._id === id) || null;

  const handleSubmit = async (userInput) => {
    try {
      const payload = {
        ...userInput,
        _id: id,
        companyId: cleanText(userInput?.companyId),
        name: cleanText(userInput?.name),
        email: cleanText(userInput?.email),
        phone: cleanText(userInput?.phone),
        address: cleanText(userInput?.address),
        industry: cleanText(userInput?.industry),
        currentPlatform: cleanText(userInput?.currentPlatform),
        website: cleanText(userInput?.website),
        contactId: cleanText(userInput?.contactId),
        leadSource: cleanText(userInput?.leadSource),
        pipelineStage: cleanText(userInput?.pipelineStage),
        relationshipStatus: cleanText(userInput?.relationshipStatus),
        leadTemperature: userInput?.leadTemperature ?? 0,
        dateContacted: userInput?.dateContacted ?? "",
        lastContactDate: userInput?.lastContactDate ?? "",
        nextFollowUpDate: userInput?.nextFollowUpDate ?? "",
        timesContacted: cleanText(userInput?.timesContacted),
        estimatedMonthlyValue: cleanText(userInput?.estimatedMonthlyValue),
        estimatedCustomersPerDay: cleanText(userInput?.estimatedCustomersPerDay),
        avgSpend: cleanText(userInput?.avgSpend),
        estimatedConversionRate: cleanText(userInput?.estimatedConversionRate),
        posPaymentOpportunity: cleanText(userInput?.posPaymentOpportunity),
        strategicPartnershipPotential: cleanText(userInput?.strategicPartnershipPotential),
        sourceWorkbook: cleanText(userInput?.sourceWorkbook),
        sourceTab: cleanText(userInput?.sourceTab),
        sourceRow: cleanText(userInput?.sourceRow),
        salesRepresentative: cleanText(userInput?.salesRepresentative),
        nextStep: cleanText(userInput.nextStep),
        phonePrefix: cleanText(userInput.phonePrefix),
        estimatedAnnualAppSales: cleanText(userInput.estimatedAnnualAppSales),
        proposedFee: cleanText(userInput.proposedFee),
        estimatedAnnualAppFee: cleanText(userInput.estimatedAnnualAppFee),
        status: cleanText(userInput.status),
        expectedCloseDate: userInput.expectedCloseDate ?? '',
        assignedTo: userInput.assignedTo || "",
        demoRejectionReason: cleanText(userInput.demoRejectionReason),
        liveRejectionReason: cleanText(userInput.liveRejectionReason),
      };
      // Only send phonePrefix when the user actually picked a country code.
      if (!payload.phonePrefix) {
        delete payload.phonePrefix;
      }

      const response = await updateSalesLead({ body: payload }).unwrap();
      successRef.current = true;
      toast.success(response?.message || "Sales lead updated successfully");
      navigate("/sales-leads");
    } catch (error) {
      captureComponentError(error, { component: "EditSalesLead", action: "handleSubmit" });
      toast.error(error?.data?.message || "Something went wrong!");
    }
  };

  return (
    <CommonPage>
      <Content>
        <PageTitle title="Update Sales Lead" />
        <LoadableContent isLoading={isFetching} isError={isError || (!isFetching && !lead)}>
          {lead && (
            <SalesLeadForm
              defaultValues={lead}
              onSubmit={handleSubmit}
              isLoading={isUpdating}
              isEdit={true}
            />
          )}
        </LoadableContent>
      </Content>
    </CommonPage>
  );
};

export default EditSalesLead;
